import { LibraryBig } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useCrmActionContracts, type CrmActionContractQueueSummary } from "@/hooks/use-crm-action-contracts";
import { useCrmEventActionContracts } from "@/hooks/use-crm-event-action-contracts";

const EMPTY_QUEUE_SUMMARY: CrmActionContractQueueSummary = {
  reviewed: 0,
  accepted: 0,
  dismissed: 0,
  readyForExecution: 0,
  manualReview: 0,
  blocked: 0,
  unknown: 0,
  materialized: 0,
  crmMaterialized: 0,
  manualResolved: 0,
  materializableReady: 0,
  manualOnlyReady: 0,
  bridgeBlocked: 0,
  inputBlockedReady: 0,
  alreadyMaterialized: 0,
  lastAcceptedAt: null,
  lastMaterializedAt: null,
};

function prettyActionLabel(value: string): string {
  return value
    .replace(/^crm\./, "")
    .replace(/@v\d+$/, "")
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function prettyOperationLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function prettySignalFamily(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

interface ActionContractRegistryProps {
  signalFamily?: string;
  sourceEventType?: string;
  onSignalFamilySelect?: (signalFamily: string) => void;
  onSourceEventTypeSelect?: (sourceEventType: string, signalFamily: string) => void;
}

export function ActionContractRegistry({
  signalFamily,
  sourceEventType,
  onSignalFamilySelect,
  onSourceEventTypeSelect,
}: ActionContractRegistryProps = {}) {
  const { data, isLoading, error, refetch } = useCrmActionContracts({
    signalFamily,
    sourceEventType,
  });
  const {
    data: eventContractsData,
    isLoading: isEventContractsLoading,
    error: eventContractsError,
    refetch: refetchEventContracts,
  } = useCrmEventActionContracts({
    signalFamily,
    sourceEventType,
  });

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="mb-4 flex items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-4 w-52" />
        </div>
        <SkeletonCard className="h-20" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-4 text-sm text-[var(--color-warning)]">
        Could not load action contracts.
        <button type="button" onClick={refetch} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  const contracts = data?.contracts ?? [];
  const summary = data?.summary ?? {
    registered: contracts.length,
    materializableFromQueue: contracts.filter((contract) => contract.materializableFromQueue).length,
    manualOnlyFromQueue: contracts.filter((contract) => contract.manualOnlyFromQueue).length,
    humanReviewGated: contracts.filter((contract) => contract.requiresHumanReview).length,
    outcomeBacked: contracts.filter((contract) => contract.observedOutcomes.length > 0).length,
    queueCoverageRate: 0,
    queueHandledCoverageRate: 0,
    outcomeCoverageRate: 0,
    queueAccepted: contracts.reduce((total, contract) => total + (contract.queue?.accepted ?? 0), 0),
    queueReadyForExecution: contracts.reduce((total, contract) => total + (contract.queue?.readyForExecution ?? 0), 0),
    queueMaterialized: contracts.reduce((total, contract) => total + (contract.queue?.materialized ?? 0), 0),
    queueCrmMaterialized: contracts.reduce((total, contract) => total + (contract.queue?.crmMaterialized ?? 0), 0),
    queueManualResolved: contracts.reduce((total, contract) => total + (contract.queue?.manualResolved ?? 0), 0),
    queueManualOnlyReady: contracts.reduce((total, contract) => total + (contract.queue?.manualOnlyReady ?? 0), 0),
    queueBridgeBlocked: contracts.reduce((total, contract) => total + (contract.queue?.bridgeBlocked ?? 0), 0),
    queueInputBlockedReady: contracts.reduce((total, contract) => total + (contract.queue?.inputBlockedReady ?? 0), 0),
    queueAlreadyMaterialized: contracts.reduce((total, contract) => total + (contract.queue?.alreadyMaterialized ?? 0), 0),
  };
  const eventContractSummary = eventContractsData?.summary;
  const topEventContracts = eventContractsData?.contracts
    .filter((contract) => contract.recommendationBacked)
    .sort((a, b) => b.recommendationCount - a.recommendationCount)
    .slice(0, 4) ?? [];
  const topTenantEventContracts = eventContractsData?.contracts
    .filter((contract) => contract.tenantTelemetry.accepted > 0 || contract.tenantTelemetry.materialized > 0)
    .sort((a, b) =>
      b.tenantTelemetry.accepted - a.tenantTelemetry.accepted
      || b.tenantTelemetry.materialized - a.tenantTelemetry.materialized)
    .slice(0, 4) ?? [];

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <LibraryBig className="h-5 w-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Action Contract Registry</h2>
          </div>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Reusable CRM action contracts with queue support, risk posture, and tenant outcome evidence.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
          {contracts.length} contracts
        </span>
      </div>
      {(signalFamily || sourceEventType) && (
        <p className="mb-3 rounded-md border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-3 py-2 text-xs text-[var(--color-primary)]">
          Registry evidence scoped to {[
            signalFamily ? `${prettySignalFamily(signalFamily)} family` : null,
            sourceEventType ? prettyActionLabel(sourceEventType) : null,
          ].filter(Boolean).join(" · ")}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">Registered</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{summary.registered}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-3">
          <p className="text-xs text-[var(--color-success)]">Queue materializable</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-success)]">{summary.materializableFromQueue}</p>
          <p className="mt-1 text-[10px] text-[var(--color-success)]">
            {percent(summary.queueCoverageRate)} executable · {percent(summary.queueHandledCoverageRate)} covered
          </p>
        </div>
        <div className="rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 p-3">
          <p className="text-xs text-[var(--color-warning)]">Human-review gated</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-warning)]">{summary.humanReviewGated}</p>
        </div>
        <div className="rounded-xl border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3 sm:col-span-3">
          <p className="text-xs text-[var(--color-primary)]">Outcome-backed contracts</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-primary)]">{summary.outcomeBacked}</p>
          <p className="mt-1 text-[10px] text-[var(--color-primary)]">{percent(summary.outcomeCoverageRate)} have tenant outcome evidence</p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 sm:col-span-3">
          <p className="text-xs text-[var(--color-muted-foreground)]">Accepted action queue</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">{summary.queueAccepted}</p>
          <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
            {summary.queueReadyForExecution} ready · {summary.queueMaterialized} materialized · {summary.queueBridgeBlocked} missing bridge
            {summary.queueManualOnlyReady > 0 ? ` · ${summary.queueManualOnlyReady} manual-only` : ""}
            {summary.queueInputBlockedReady > 0 ? ` · ${summary.queueInputBlockedReady} missing inputs` : ""}
            {summary.queueManualResolved > 0 ? ` · ${summary.queueManualResolved} manually resolved` : ""}
            {summary.queueAlreadyMaterialized > 0 ? ` · ${summary.queueAlreadyMaterialized} already done` : ""}
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
              Event scoring coverage
            </p>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              CRM event contracts mapped to reusable score, action, and queue recommendations.
            </p>
            {eventContractsData && (
              <p className="mt-1 text-[10px] text-[var(--color-muted-foreground)]">
                Tenant telemetry window: {eventContractsData.windowDays}d
              </p>
            )}
          </div>
          {eventContractSummary && (
            <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-1 text-xs text-[var(--color-primary)]">
              {percent(eventContractSummary.eventCoverageRate)} event-backed
            </span>
          )}
        </div>
        {isEventContractsLoading && (
          <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">Loading event scoring contracts...</p>
        )}
        {eventContractsError && (
          <p className="mt-3 text-xs text-[var(--color-warning)]">
            Could not load event scoring contracts.
            <button type="button" onClick={refetchEventContracts} className="ml-2 underline">Retry</button>
          </p>
        )}
        {eventContractSummary && (
          <div className="mt-3 grid gap-2 sm:grid-cols-4">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2">
              <p className="text-[10px] text-[var(--color-muted-foreground)]">Registered events</p>
              <p className="text-lg font-semibold text-[var(--color-foreground)]">{eventContractSummary.registeredEvents}</p>
            </div>
            <div className="rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-2 py-2">
              <p className="text-[10px] text-[var(--color-success)]">Recommendation-backed</p>
              <p className="text-lg font-semibold text-[var(--color-success)]">{eventContractSummary.recommendationBackedEvents}</p>
            </div>
            <div className="rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-2">
              <p className="text-[10px] text-[var(--color-primary)]">Queue-actionable</p>
              <p className="text-lg font-semibold text-[var(--color-primary)]">{eventContractSummary.queueActionableEvents}</p>
            </div>
            <div className="rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-2">
              <p className="text-[10px] text-[var(--color-primary)]">Tenant-backed</p>
              <p className="text-lg font-semibold text-[var(--color-primary)]">{eventContractSummary.telemetryBackedEvents}</p>
              <p className="text-[10px] text-[var(--color-primary)]">
                {eventContractSummary.acceptedBySourceEvents} accepted · {eventContractSummary.materializedBySourceEvents} materialized
              </p>
            </div>
            <div className="rounded-lg border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-2 py-2">
              <p className="text-[10px] text-[var(--color-warning)]">Uncovered</p>
              <p className="text-lg font-semibold text-[var(--color-warning)]">{eventContractSummary.uncoveredEvents}</p>
            </div>
          </div>
        )}
        {eventContractSummary && eventContractSummary.signalFamilies.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {eventContractSummary.signalFamilies.map((family) => (
              <button
                key={family}
                type="button"
                onClick={() => onSignalFamilySelect?.(family)}
                className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
              >
                {prettySignalFamily(family)}
              </button>
            ))}
          </div>
        )}
        {topTenantEventContracts.length > 0 && (
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {topTenantEventContracts.map((contract) => (
              <button
                key={`tenant:${contract.signalKey}`}
                type="button"
                onClick={() => onSourceEventTypeSelect?.(contract.eventType, contract.signalFamily)}
                className="rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-2 text-left text-xs transition-colors hover:border-[var(--color-primary)]"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-[var(--color-foreground)]">
                    {contract.activityType ? `${contract.eventType}:${contract.activityType}` : contract.eventType}
                  </p>
                  <span className="rounded-full border border-[var(--color-primary)]/20 px-2 py-0.5 text-[var(--color-primary)]">
                    {contract.tenantTelemetry.accepted} accepted
                  </span>
                </div>
                <p className="mt-1 text-[var(--color-muted-foreground)]">
                  {prettySignalFamily(contract.signalFamily)} · {contract.tenantTelemetry.materialized} materialized
                  {contract.tenantTelemetry.topActionRefs[0]
                    ? ` · Top: ${prettyActionLabel(contract.tenantTelemetry.topActionRefs[0].actionRef)}`
                    : ""}
                </p>
              </button>
            ))}
          </div>
        )}
        {topEventContracts.length > 0 && (
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {topEventContracts.map((contract) => (
              <button
                key={contract.signalKey}
                type="button"
                onClick={() => onSourceEventTypeSelect?.(contract.eventType, contract.signalFamily)}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/20 px-2 py-2 text-left text-xs transition-colors hover:border-[var(--color-primary)]"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-[var(--color-foreground)]">
                    {contract.activityType ? `${contract.eventType}:${contract.activityType}` : contract.eventType}
                  </p>
                  <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-muted-foreground)]">
                    {contract.recommendationCount} recs
                  </span>
                </div>
                <p className="mt-1 text-[var(--color-muted-foreground)]">
                  {prettySignalFamily(contract.signalFamily)} · Actions: {contract.actionRefs.slice(0, 4).map(prettyActionLabel).join(", ")}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {contracts.slice(0, 5).map((contract) => {
          const bestOutcome = contract.observedOutcomes[0];
          const queueMaterialization = contract.queueMaterialization;
          const queueManualResolution = contract.queueManualResolution;
          const queue = contract.queue ?? EMPTY_QUEUE_SUMMARY;

          return (
            <div
              key={contract.ref}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2 text-xs"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-[var(--color-foreground)]">{prettyActionLabel(contract.ref)}</p>
                  <p className="text-[var(--color-muted-foreground)]">{contract.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {contract.materializableFromQueue && (
                    <span className="rounded-full border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-2 py-0.5 text-[var(--color-success)]">
                      Queue bridge
                    </span>
                  )}
                  {queueMaterialization && (
                    <span className="rounded-full border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 px-2 py-0.5 text-[var(--color-primary)]">
                      {queueMaterialization.entityType} {queueMaterialization.method}
                    </span>
                  )}
                  {contract.manualOnlyFromQueue && (
                    <span className="rounded-full border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-2 py-0.5 text-[var(--color-warning)]">
                      Manual-only
                    </span>
                  )}
                  <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-[var(--color-muted-foreground)]">
                    {contract.aiClassification} risk
                  </span>
                </div>
              </div>
              {queueMaterialization && (
                <div className="mt-2 rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/30 px-2 py-1.5 text-[var(--color-muted-foreground)]">
                  <p>
                    Bridge: {prettyOperationLabel(queueMaterialization.operation)} · idempotent by {queueMaterialization.idempotencyKey}
                  </p>
                  <p className="mt-1 truncate" title={queueMaterialization.endpoint}>
                    Endpoint: {queueMaterialization.endpoint}
                  </p>
                  <p className="mt-1">
                    Required: {queueMaterialization.requiredInputPaths.join(", ")}
                  </p>
                  <p className="mt-1">
                    Inputs: {queueMaterialization.supportedInputPaths.join(", ")}
                    {queueMaterialization.supportedFields?.length
                      ? ` · Fields: ${queueMaterialization.supportedFields.join(", ")}`
                      : ""}
                  </p>
                  {queueMaterialization.targetFallbackPaths?.length ? (
                    <p className="mt-1">
                      Target fallback: {queueMaterialization.targetFallbackPaths.join(" → ")}
                    </p>
                  ) : null}
                </div>
              )}
              {queueManualResolution && (
                <div className="mt-2 rounded-md border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/5 px-2 py-1.5 text-[var(--color-warning)]">
                  <p>{queueManualResolution.reason}</p>
                  <p className="mt-1">Manual path: {queueManualResolution.operatorAction}</p>
                </div>
              )}
              {bestOutcome && (
                <p className="mt-2 text-[var(--color-muted-foreground)]">
                  Best observed intent: {prettyActionLabel(bestOutcome.intent)} · {percent(bestOutcome.materializationRate)} materialized · {bestOutcome.confidence} confidence
                </p>
              )}
              {queue.reviewed > 0 && (
                <p className="mt-2 text-[var(--color-muted-foreground)]">
                  Queue: {queue.accepted} accepted · {queue.readyForExecution} ready · {queue.manualReview} review · {queue.blocked} blocked · {queue.materialized} materialized
                  {queue.crmMaterialized > 0 || queue.manualResolved > 0
                    ? ` (${queue.crmMaterialized} CRM, ${queue.manualResolved} manual)`
                    : ""}
                  {queue.inputBlockedReady > 0 ? ` · ${queue.inputBlockedReady} missing inputs` : ""}
                  {queue.alreadyMaterialized > 0 ? ` · ${queue.alreadyMaterialized} already done` : ""}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
